import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { find, get, isEmpty, isNull, noop, omitBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { PAGE_START } from '~/common/constants/page';
import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import * as Filters from '~/components/Shared/Filters_DEPRECATED/types';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import { EnumValue } from '~/evifields/src/types/Value';
import { api } from '~/redux/api/createApi';
import {
  DeletedDocumentDeletedByUserOption,
  DeletedDocumentOriginalFolderOption,
} from '~/redux/api/methods/deletedDocument';
import { TableQuery } from '~/types/DocumentRestore';
import { debounce } from '~/utils/debounce';

type DocumentsRestoreFiltersProps = {
  query: TableQuery;
  onChange: any;
  isAdminConsole: boolean;
};

type DocumentRestoreFiltersDefaultField = {
  id: string;
  label: string;
  type: string;
  settings: {
    __testEnableSelectAll?: (_: Filter<EnumValue>) => boolean;
    options?:
      | DeletedDocumentOriginalFolderOption[]
      | DeletedDocumentDeletedByUserOption[];
  };
  whitelistOperatorIds?: OperatorId[];
  render?: Filters.FieldRendererProps;
};

type DocumentRestoreFiltersDefaultFieldNames =
  | 'doc_name'
  | 'folder'
  | 'deleted_date'
  | 'deleted_by';

type DocumentRestoreFiltersDefaultFields = {
  [key in DocumentRestoreFiltersDefaultFieldNames]?: DocumentRestoreFiltersDefaultField;
};

export const DocumentsRestoreFilters = ({
  query,
  onChange = noop,
  isAdminConsole = false,
}: DocumentsRestoreFiltersProps) => {
  const intl = useIntl();

  const getDeletedDocumentsOriginalFolderEndpoint = isAdminConsole
    ? api.endpoints.getDeletedDocumentsOriginalFolder
    : api.endpoints.getDeletedDocumentsOriginalFolderForCurrentUser;

  const { data: folders, isFetching: isFetchingFolders } =
    getDeletedDocumentsOriginalFolderEndpoint.useQuery({
      substringQuery: '',
    });

  const { data: users, isFetching: isFetchingUsers } =
    api.endpoints.getDeletedDocumentsDeletedByUsers.useQuery(undefined);

  const AsyncFolderOptionsRenderer: Filters.FieldRendererProps = ({
    Component,
    innerProps,
  }) => {
    const [getFieldValues, result] =
      getDeletedDocumentsOriginalFolderEndpoint.useLazyQuery();
    const debouncedGetFieldValues = useCallback(debounce(getFieldValues, 500), [
      getFieldValues,
    ]);
    const { data, error, isFetching } = result;
    const options = (data || []).map((value) => ({
      value: value.value,
      label: value.display_value,
      description: value.path,
      leftIcon: 'folder',
    }));
    const asyncOptions = { data: options, error, isFetching };
    const fetchOptions = (substringQuery = '') => {
      debouncedGetFieldValues({ substringQuery }, true);
    };

    const asyncProps = {
      asyncOptions,
      onMount: fetchOptions,
      onQuery: fetchOptions,
    };

    return <Component asyncProps={asyncProps} innerProps={innerProps} />;
  };

  const DEFAULT_FIELDS: DocumentRestoreFiltersDefaultFields = {
    doc_name: {
      id: 'doc_name',
      label: intl.formatMessage({
        id: 'DocumentsRestoreFilters.nameLabel',
        defaultMessage: 'Name',
        description: 'Label for document name filter',
      }),
      type: 'text',
      whitelistOperatorIds: ['text_contains_any'],
      settings: {},
    },
    folder: {
      id: 'folder',
      label: intl.formatMessage({
        id: 'DocumentsRestoreFilters.originalFolderLabel',
        defaultMessage: 'Original Folder',
        description: 'Label for original folder filter',
      }),
      type: 'folder',
      settings: {
        __testEnableSelectAll: (_: Filter<EnumValue>) => false,
        options: isFetchingFolders ? [] : folders,
      },
      render: AsyncFolderOptionsRenderer,
    },
    deleted_date: {
      id: 'deleted_date',
      label: intl.formatMessage({
        id: 'DocumentsRestoreFilters.deletedDateLabel',
        defaultMessage: 'Deleted date',
        description: 'Label for deleted date filter',
      }),
      type: 'date',
      whitelistOperatorIds: [
        'date_on',
        'date_before',
        'date_after',
        'date_between',
      ],
      settings: {},
    },
  };
  if (isAdminConsole) {
    DEFAULT_FIELDS.deleted_by = {
      id: 'deleted_by',
      label: intl.formatMessage({
        id: 'DocumentsRestoreFilters.deletedByLabel',
        defaultMessage: 'Deleted by',
        description: 'Label for deleted by filter',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        __testEnableSelectAll: (_: Filter<EnumValue>) => false,
        options: isFetchingUsers ? [] : users,
      },
    };
  }

  const DEFAULT_FILTERS = [
    {
      id: uuid(),
      fieldId: 'doc_name',
      operatorId: null,
      values: [],
    },
    {
      fieldId: 'folder',
      id: uuid(),
      operatorId: null,
      values: [],
      asyncValue: {
        isAllSelected: false,
        length: 0,
        search: '',
        selectedValuesMap: {},
      },
    },
    {
      fieldId: 'deleted_date',
      id: uuid(),
      operatorId: null,
      values: [],
    },
  ];
  if (isAdminConsole) {
    DEFAULT_FILTERS.push({
      fieldId: 'deleted_by',
      id: uuid(),
      operatorId: null,
      values: [],
    });
  }

  const [filters, setFilters] = useState(DEFAULT_FILTERS as Filter[]);

  useEffect(() => {
    if (isEmpty(query.filters)) setFilters(DEFAULT_FILTERS as Filter[]);
  }, [query]);

  const parseDateFilter = (ft: Filter | undefined) => {
    if (!ft) return {};

    switch (ft.operatorId) {
      case 'date_on':
        return {
          deleted_date__gte: formatISO(startOfDay(ft.values[0] as Date)),
          deleted_date__lte: formatISO(endOfDay(ft.values[0] as Date)),
        };
      case 'date_before':
        return {
          deleted_date__lte: formatISO(endOfDay(ft.values[0] as Date)),
        };
      case 'date_after':
        return {
          deleted_date__gte: formatISO(startOfDay(ft.values[0] as Date)),
        };
      case 'date_between':
        return {
          deleted_date__gte: formatISO(startOfDay(ft.values[0] as Date)),
          deleted_date__lte: formatISO(endOfDay(ft.values[1] as Date)),
        };
    }
  };

  const onChangeFilter = (fts: Filter[]) => {
    setFilters(fts);

    const filters = {
      ...parseDateFilter(find(fts, (f) => f.fieldId === 'deleted_date')),
      name: get(
        find(fts, (f) => f.fieldId === 'doc_name'),
        'values',
      ),
      originalFolder: get(
        find(fts, (f) => f.fieldId === 'folder'),
        'values',
      ),
      deletedBy: get(
        find(fts, (f) => f.fieldId === 'deleted_by'),
        'values',
      ),
    };

    onChange({ ...query, page: PAGE_START, filters: omitBy(filters, isNull) });
  };

  return (
    <Filters_DEPRECATED
      fields={DEFAULT_FIELDS as Record<FieldId, Field>}
      filters={filters}
      groups={[]}
      options={{ disableManageFilters: true, disableRemoveFilter: true }}
      onChange={onChangeFilter}
    />
  );
};
